import Grid from '@mui/material/Grid2';
import SMTChart from '../common/SMTChart';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import SMTInfo from '../common/SMTInfo';

type ParamItem = string | Record<string, unknown>;

export interface CallContentConfig {
  name: string;
  title?: string;
  url: string;
  method?: string;
  size?: number;
  pattern?: string;
  upload?: unknown;
  isHidden?: string;
  revertHidden?: boolean;
  get_data_from_api?: string;
  data_type?: string;
  params?: ParamItem[] | Record<string, unknown>;
  body?: string[];
  content?: { component?: string; properties?: Record<string, unknown> };
}

const CallContent = ({ field }: { field: CallContentConfig }) => {
  const { getFormDataItem } = useFormContext();
  const formData = useFormData();

  let content = <></>;
  let isHidden = Object.prototype.hasOwnProperty.call(field, 'isHidden') ? getFormDataItem(formData, field.isHidden!) : false;

  if (Object.prototype.hasOwnProperty.call(field, 'revertHidden') && field?.revertHidden) {
    isHidden = !isHidden;
  }

  if (isHidden) {
    return null;
  }

  switch (field?.content?.component) {
    case 'chart': {
      let requestParams;

      if (Array.isArray(field?.params)) {
        requestParams = field.params.reduce<Record<string, unknown>>((acc, item) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            Object.assign(acc, item);
            return acc;
          }

          if (typeof item === 'string') {
            const value = getFormDataItem(formData, item);
            const keyName = item.split('.').pop() || item;
            if (typeof value === 'object') {
              Object.assign(acc, value);
            } else {
              Object.assign(acc, { [keyName]: value });
            }
          }

          return acc;
        }, {});
      } else if (field?.params && typeof field.params === 'object') {
        requestParams = { ...field.params };
      }

      const body = field?.body?.reduce<Record<string, unknown>>((acc, key) => {
        const value = getFormDataItem(formData, key);
        if (typeof value === 'object') {
          Object.assign(acc, value);
        } else {
          Object.assign(acc, { [key]: value });
        }
        // Object.assign(acc, {[key]: value})
        return acc;
      }, {});
      // yield-evaluation and soiling-ratios always render their chart (they read everything from the
      // project / handle empty state themselves); other charts are hidden while their pattern is an
      // un-validated uploaded file (a string path).
      const shouldShowChart =
        field.url === 'tools/yield-evaluation' || field.url === 'soiling-ratios' ? true : typeof body?.[field.pattern!] !== 'string';

      content = shouldShowChart ? (
        <SMTChart
          upload={field?.upload}
          properties={field?.content?.properties}
          method={field?.method ?? 'get'}
          params={requestParams}
          body={{}}
          url={field.url}
          title={field?.title}
          getDataFromApi={field?.get_data_from_api ? getFormDataItem(formData, field.get_data_from_api) : false}
          data={getFormDataItem(formData, field.name)}
          data_type={field?.data_type}
        />
      ) : (
        <></>
      );
      break;
    }
    case 'info': {
      let infoParams;

      if (Array.isArray(field?.params)) {
        infoParams = field.params.reduce<Record<string, unknown>>((acc, item) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            Object.assign(acc, item);
            return acc;
          }

          if (typeof item === 'string') {
            const value = getFormDataItem(formData, item);
            if (typeof value === 'object') {
              Object.assign(acc, value);
            } else {
              Object.assign(acc, { [item]: value });
            }
          }

          return acc;
        }, {});
      } else if (field?.params && typeof field.params === 'object') {
        infoParams = { ...field.params };
      }
      return (
        <SMTInfo
          properties={field?.content?.properties}
          method={field?.method ?? 'get'}
          params={infoParams}
          body={{}}
          url={field.url}
          title={field?.title}
        />
      );
    }
    default:
      content = <span>Unknown content_type</span>;
  }
  return (
    <Grid size={field.size || 12} key={field.name}>
      {content}
    </Grid>
  );
};

export default CallContent;
